import { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { getFileNameFromUrl, myAxios } from "../common/functions";
import { saveAs } from "file-saver";
import { ContextMenuProps, ContextMenuState, FileItem, WorkOrder } from "./types";
import { useRevalidator } from "react-router-dom";
import { useUploadStore } from "../main/uploadStore";
// @ts-ignore: side-effect import for CSS module handled by bundler
import "./fileSystem.css";
import FileIcon from "../common/fileIcons";

// ─── Types ────────────────────────────────────────────────────────────────────

interface DragState {
    active: boolean;
    startX: number; // clientX at mousedown (viewport coords)
    startY: number; // clientY at mousedown (viewport coords)
    currentX: number;
    currentY: number;
}

interface SelectionRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

// ─── FileSystem ───────────────────────────────────────────────────────────────

export default function WorkOrderFileSystem({ workOrder }: { workOrder: WorkOrder }) {
    const revalidator = useRevalidator();
    const upload = useUploadStore();
    const { id, workOrderFiles } = workOrder;
    const [selected, setSelected] = useState<number[]>([]);
    const [contextMenu, setContextMenu] = useState<ContextMenuState>({ show: false });

    // Drag-select state
    const [dragState, setDragState] = useState<DragState | null>(null);
    const [selectionRect, setSelectionRect] = useState<SelectionRect | null>(null);
    const fileRefs = useRef<(HTMLDivElement | null)[]>([]);
    const containerRef = useRef<HTMLFieldSetElement | null>(null);
    const isDragging = useRef(false);

    const closeMenu = () => {
        setSelected([]);
        setContextMenu({ show: false });
    };

    // ─── Drag-select logic ────────────────────────────────────────────────────

    const getSelectionRect = useCallback((ds: DragState): SelectionRect => {
        // Everything in viewport (client) coords — no scroll involved
        return {
            left: Math.min(ds.startX, ds.currentX),
            top: Math.min(ds.startY, ds.currentY),
            width: Math.abs(ds.currentX - ds.startX),
            height: Math.abs(ds.currentY - ds.startY),
        };
    }, []);

    const rectsIntersect = (
        r1: SelectionRect,
        r2: { left: number; top: number; right: number; bottom: number }
    ) => {
        return !(
            r1.left > r2.right ||
            r1.left + r1.width < r2.left ||
            r1.top > r2.bottom ||
            r1.top + r1.height < r2.top
        );
    };

    const getSelectedFromRect = useCallback(
        (rect: SelectionRect): number[] => {
            const hits: number[] = [];
            fileRefs.current.forEach((el, idx) => {
                if (!el) return;
                const bounding = el.getBoundingClientRect();
                // getBoundingClientRect is already in viewport (client) coords
                const elRect = {
                    left: bounding.left,
                    top: bounding.top,
                    right: bounding.right,
                    bottom: bounding.bottom,
                };
                if (rectsIntersect(rect, elRect)) hits.push(idx);
            });
            return hits;
        },
        []
    );

    const handleContainerMouseDown = useCallback(
        (e: React.MouseEvent<HTMLFieldSetElement>) => {
            // Only left button, not on a file icon itself
            if (e.button !== 0) return;
            if ((e.target as HTMLElement).closest(".fs-file")) return;
            if (navigator.maxTouchPoints) return;

            e.preventDefault();
            isDragging.current = false;

            const startX = e.clientX;
            const startY = e.clientY;

            setDragState({
                active: false,
                startX,
                startY,
                currentX: startX,
                currentY: startY,
            });
        },
        []
    );

    useEffect(() => {
        if (dragState === null) return;

        const onMouseMove = (e: MouseEvent) => {
            const currentX = e.clientX;
            const currentY = e.clientY;

            setDragState(prev => {
                if (!prev) return null;
                const dx = currentX - prev.startX;
                const dy = currentY - prev.startY;
                const movedEnough = Math.abs(dx) > 4 || Math.abs(dy) > 4;

                if (!isDragging.current && movedEnough) {
                    isDragging.current = true;
                }

                if (!isDragging.current) return prev;

                const next = { ...prev, active: true, currentX, currentY };
                const rect = getSelectionRect(next);
                setSelectionRect(rect);
                setSelected(getSelectedFromRect(rect));
                return next;
            });
        };

        const onMouseUp = () => {
            if (!isDragging.current) {
                // It was just a plain click on the background — deselect
                setSelected([]);
            }
            isDragging.current = false;
            setDragState(null);
            setSelectionRect(null);
        };

        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
        };
    }, [dragState, getSelectionRect, getSelectedFromRect]);

    // ─── Upload handlers ──────────────────────────────────────────────────────

    const handleFilesInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const target = e.currentTarget;
        const files = target.files;
        if (!files || files.length === 0 || files[0].name === "") return;
        const formData = new FormData();
        formData.append("id", String(id));
        for (const file of files) {
            formData.append("fileName[]", file.name);
            formData.append("fileSize[]", String(file.size));
            formData.append("fileType[]", file.type);
            formData.append("lastModifiedAt[]", new Date(file.lastModified).toISOString().slice(0, 19).replace("T", " "));
        }
        const res = await myAxios({ fileName: "/workOrders/createAttachment.php", formData });
        if (!res) return;
        upload.setFiles(Array.from(files));
        upload.setUrls(res.data.urls);
        upload.setIds(res.data.ids);
        upload.setShow(true);
        upload.setOnResetCallback(() => { revalidator.revalidate(); });
        target.value = "";
    };

    const handleDrop = async (e: React.DragEvent<HTMLFieldSetElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (navigator.maxTouchPoints) return;
        e.currentTarget.classList.remove("fs-drag-over");
        const files = e.dataTransfer.files;
        if (!files.length) { alert("Only drop files here."); return; }
        const formData = new FormData();
        formData.append("id", String(id));
        for (const file of files) {
            formData.append("fileName[]", file.name);
            formData.append("fileSize[]", String(file.size));
            formData.append("fileType[]", file.type);
            formData.append("lastModifiedAt[]", new Date(file.lastModified).toISOString().slice(0, 19).replace("T", " "));
        }
        const res = await myAxios({ fileName: "/workOrders/createAttachment.php", formData });
        if (!res) return;
        upload.setFiles(Array.from(files));
        upload.setUrls(res.data.urls);
        upload.setIds(res.data.ids);
        upload.setShow(true);
        upload.setOnResetCallback(() => { revalidator.revalidate(); });
    };

    // ─── File interaction handlers ────────────────────────────────────────────

    const handleDownload = async (file: FileItem) => {
        const formData = new FormData();
        formData.append("id", String(id));
        formData.append("fileId[]", file.id);
        formData.append("fileName[]", file.name);
        const res = await myAxios({ fileName: "/workOrders/getAttachment.php", formData });
        if (!res) return;
        for (const url of res.data) {
            try {
                const response = await fetch(url);
                if (!response.ok) throw new Error("Failed to fetch file");
                saveAs(await response.blob(), getFileNameFromUrl(url)!);
            } catch (error) {
                window.alert("Error downloading file:" + error);
            }
        }
        revalidator.revalidate();
    };

    const toggleSelect = (key: number, ctrl: boolean) => {
        setContextMenu({ show: false });
        if (ctrl) {
            setSelected(prev => prev.includes(key) ? prev.filter(v => v !== key) : [...prev, key]);
        } else {
            setSelected([key]);
        }
    };

    const openContextAt = (e: React.MouseEvent, key: number) => {
        e.preventDefault();
        e.stopPropagation();
        if (!selected.includes(key)) setSelected([key]);
        setContextMenu({
            show: true,
            top: e.clientY + window.scrollY,
            left: e.clientX + window.scrollX,
            workOrderFiles,
        });
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <div
            className="fs-root"
            onClick={closeMenu}
            onContextMenu={e => { e.preventDefault(); closeMenu(); }}
        >
            <fieldset
                ref={containerRef}
                className="fs-section"
                style={{ userSelect: "none", position: "relative" }}
                onClick={e => { e.stopPropagation(); }}
                onContextMenu={e => { e.preventDefault(); e.stopPropagation(); closeMenu(); }}
                onMouseDown={handleContainerMouseDown}
                onDragOver={e => { e.preventDefault(); e.stopPropagation(); if (!navigator.maxTouchPoints) e.currentTarget.classList.add("fs-drag-over"); }}
                onDragLeave={e => { e.stopPropagation(); e.currentTarget.classList.remove("fs-drag-over"); }}
                
                onDrop={handleDrop}
            >
                <legend className="fs-legend">Files</legend>

                {/* Rubber-band selection overlay */}
                {selectionRect && selectionRect.width > 2 && selectionRect.height > 2 &&  createPortal(
                    <div
                        style={{
                            position: "fixed",
                            left: selectionRect.left,
                            top: selectionRect.top,
                            width: selectionRect.width,
                            height: selectionRect.height,
                            border: "1.5px solid #3b82f6",
                            background: "rgba(59,130,246,0.12)",
                            pointerEvents: "none",
                            zIndex: 9999,
                            borderRadius: 2,
                        }}
                    />, document.getElementById("app-root")!
                )}

                <div className="fs-files">
                    {workOrderFiles.length === 0 && (
                        <span className="fs-empty">No files</span>
                    )}
                    {workOrderFiles.map((file, fileIdx) => {
                        const isSelected = selected.includes(fileIdx);
                        const ext = file.name.split(".").pop() ?? "";
                        return (
                            <div
                                key={fileIdx}
                                ref={el => { fileRefs.current[fileIdx] = el; }}
                                className={`fs-file ${isSelected ? "fs-file--selected" : ""}`}
                                title={file.name}
                                onClick={e => {
                                    e.stopPropagation();
                                    // Suppress click if we just finished a drag
                                    if (isDragging.current) return;
                                    toggleSelect(fileIdx, e.ctrlKey || e.metaKey);
                                    if (navigator.maxTouchPoints) openContextAt(e, fileIdx);
                                }}
                                onDoubleClick={() => handleDownload(file)}
                                onContextMenu={e => { if (navigator.maxTouchPoints) return; openContextAt(e, fileIdx); }}
                            >
                                <div className="fs-file__icon">
                                    <span className="fs-file__ext">{FileIcon({fileType: ext, isFolder: false})}</span>
                                </div>
                                <span className="fs-file__name">
                                    {file.name.length > 12 ? file.name.substring(0, 10) + "…" : file.name}
                                </span>
                            </div>
                        );
                    })}
                </div>

                {navigator.maxTouchPoints ? (
                    <div className="fs-upload-bar">
                        <button
                            type="button"
                            className="fs-upload-btn"
                            onClick={e => (e.currentTarget.nextSibling as HTMLInputElement).click()}
                        >
                            <UploadIcon /> Upload
                        </button>
                        <input type="file" multiple style={{ display: "none" }} onChange={handleFilesInput} />
                    </div>
                ) : (
                    <div className="fs-drop-hint">Drop files here · Drag to select · Right-click to manage</div>
                )}
            </fieldset>

            {contextMenu.show && (
                <ContextMenu
                    {...contextMenu}
                    selected={selected}
                    setSelected={setSelected}
                    setContextMenu={setContextMenu}
                    id={id}
                />
            )}
        </div>
    );
}

// ─── ContextMenu ──────────────────────────────────────────────────────────────

function ContextMenu({ top, left, workOrderFiles, selected, setSelected, setContextMenu, id }: ContextMenuProps) {
    const revalidator = useRevalidator();
    const close = () => { setSelected([]); setContextMenu({ show: false }); };

    const handleDownload = async () => {
        const formData = new FormData();
        formData.append("id", String(id));
        selected.forEach(s => formData.append("fileId[]", workOrderFiles![s].id));
        selected.forEach(s => formData.append("fileName[]", workOrderFiles![s].name));
        const res = await myAxios({ fileName: "/workOrders/getAttachment.php", formData });
        if (!res) return;
        for (const url of res.data) {
            try {
                const response = await fetch(url);
                if (!response.ok) throw new Error("Failed to fetch file");
                saveAs(await response.blob(), getFileNameFromUrl(url)!);
            } catch (error) {
                window.alert("Error downloading file:" + error);
            }
        }
        revalidator.revalidate();
        close();
    };

    const handleDelete = async () => {
        if (!window.confirm("Are you sure you want to delete?")) return;
        const formData = new FormData();
        formData.append("id", String(id));
        selected.forEach(s => formData.append("fileId[]", workOrderFiles![s].id));
        selected.forEach(s => formData.append("fileName[]", workOrderFiles![s].name));
        const res = await myAxios({ fileName: "/workOrders/deleteAttachment.php", formData });
        if (!res) return;
        revalidator.revalidate();
        close();
    };

    return createPortal(
        <div className="fs-context-menu" style={{ top, left }}>
            <button className="fs-context-menu__item" onClick={handleDownload}>Download</button>
            <button className="fs-context-menu__item fs-context-menu__item--danger" onClick={handleDelete}>Delete</button>
            <button className="fs-context-menu__item fs-context-menu__item--cancel" onClick={close}>Cancel</button>
        </div>,
        document.getElementById("app-root")!
    );
}

// ─── Icon ─────────────────────────────────────────────────────────────────────

function UploadIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="16 16 12 12 8 16" />
            <line x1="12" y1="12" x2="12" y2="21" />
            <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
        </svg>
    );
}